import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Chip from '@mui/material/Chip';
//import { useTheme } from '@mui/material/styles';
//import useMediaQuery from '@mui/material/useMediaQuery';

// Mock data for case studies
const caseStudies = [
  {
    id: 1,
    title: 'Overcoming Sprint Disruptions',
    industry: 'Software Development',
    challenge: 'A team consistently faced disruptions from stakeholders during sprints, leading to missed goals.',
    solution: 'Implemented a buffer system for urgent requests and educated stakeholders on the impact of mid-sprint changes.',
    outcome: 'Sprint success rate improved from 60% to 90% within three months.',
    tags: ['Sprint Planning', 'Stakeholder Management']
  },
  {
    id: 2,
    title: 'Scaling Scrum Across Teams',
    industry: 'E-commerce',
    challenge: 'A growing company needed to scale from 2 to 8 Scrum teams while maintaining coordination.',
    solution: 'Implemented Scrum of Scrums and established clear dependencies management across teams.',
    outcome: 'Successfully scaled while improving overall delivery predictability.',
    tags: ['Scaling', 'Multi-team Coordination']
  },
  {
    id: 3,
    title: 'Improving Retrospective Effectiveness',
    industry: 'Healthcare',
    challenge: 'Team retrospectives became repetitive and failed to drive meaningful improvements.',
    solution: 'Introduced varied retrospective formats and established a system to track action items to completion.',
    outcome: 'Team engagement increased and 80% of retrospective actions were completed, up from 30%.',
    tags: ['Retrospectives', 'Continuous Improvement']
  },
  {
    id: 4,
    title: 'Transitioning from Waterfall to Scrum',
    industry: 'Financial Services',
    challenge: 'A traditional financial institution struggled with the cultural shift from waterfall to Scrum.',
    solution: 'Implemented a phased approach with extensive training and executive sponsorship.',
    outcome: 'Successfully transitioned 12 teams over 18 months with measurable improvements in delivery time.',
    tags: ['Transformation', 'Change Management']
  }
];

// Mock data for simulations
const simulations = [
  {
    id: 1,
    title: 'Handle a Missed Sprint Goal',
    description: 'Interactive scenario where you must address a team that has missed their sprint goal for the third time.',
    difficulty: 'Medium',
    estimatedTime: '15 minutes'
  },
  {
    id: 2,
    title: 'Prioritize a Chaotic Backlog',
    description: 'Work with stakeholders to bring order to a disorganized product backlog with competing priorities.',
    difficulty: 'Hard',
    estimatedTime: '20 minutes'
  },
  {
    id: 3,
    title: 'Facilitate a Difficult Retrospective',
    description: 'Guide a team through a retrospective after a particularly challenging sprint with conflicts.',
    difficulty: 'Medium',
    estimatedTime: '15 minutes'
  }
];

const CaseStudies = () => {
  //const theme = useTheme();
  //const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
 // const isMediumScreen = useMediaQuery(theme.breakpoints.up('md'));
  
  const [tabValue, setTabValue] = useState(0);
  const [selectedCase, setSelectedCase] = useState(null);
  const [caseDialogOpen, setCaseDialogOpen] = useState(false);
  const [simulationDialogOpen, setSimulationDialogOpen] = useState(false);
  const [selectedSimulation, setSelectedSimulation] = useState(null);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCaseClick = (caseStudy) => {
    setSelectedCase(caseStudy);
    setCaseDialogOpen(true);
  };

  const handleSimulationClick = (simulation) => {
    setSelectedSimulation(simulation);
    setSimulationDialogOpen(true);
  };

  return (
    <>
      <Helmet>
        <title>Case Studies &amp; Simulations | ScrumTherapist</title>
        <link rel="canonical" href="https://www.scrumtherapist.com/case-studies" />
        <meta name="description" content="Real-world Scrum case studies and interactive simulations to practice problem-solving." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": "https://www.scrumtherapist.com/case-studies#webpage",
            "url": "https://www.scrumtherapist.com/case-studies",
            "name": "Case Studies & Simulations",
            "description": "Real-world Scrum case studies and interactive simulations to practice problem-solving.",
            "isPartOf": { "@id": "https://www.scrumtherapist.com/#website" }
          })}
        </script>
      </Helmet>
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom align="center" color="primary.dark">
          Real-World Scenarios & Case Studies
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" paragraph>
          Learn from real experiences and practice with interactive simulations
        </Typography>
      </Box>

      <Box sx={{ width: '100%', mb: 4 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          centered
        >
          <Tab label="Case Studies" />
          <Tab label="Interactive Simulations" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <Grid container spacing={4}>
          {caseStudies.map((caseStudy) => (
            <Grid item key={caseStudy.id} xs={12} md={6}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography gutterBottom variant="h5" component="h2">
                    {caseStudy.title}
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Industry: {caseStudy.industry}
                  </Typography>
                  <Typography variant="body2" paragraph>
                    <strong>Challenge:</strong> {caseStudy.challenge}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {caseStudy.tags.map((tag) => (
                      <Chip key={tag} label={tag} size="small" sx={{ mr: 1, mb: 1 }} />
                    ))}
                  </Box>
                </CardContent>
                <CardActions>
                  <Button size="small" onClick={() => handleCaseClick(caseStudy)}>Read More</Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {tabValue === 1 && (
        <Grid container spacing={4}>
          {simulations.map((simulation) => (
            <Grid item key={simulation.id} xs={12} md={4}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography gutterBottom variant="h5" component="h2">
                    {simulation.title}
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {simulation.description}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Chip label={`Difficulty: ${simulation.difficulty}`} size="small" />
                    <Chip label={`Time: ${simulation.estimatedTime}`} size="small" />
                  </Box>
                </CardContent>
                <CardActions>
                  <Button size="small" onClick={() => handleSimulationClick(simulation)}>Start Simulation</Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      
      <Dialog open={caseDialogOpen} onClose={() => setCaseDialogOpen(false)} maxWidth="md" fullWidth>
        {selectedCase && (
          <>
            <DialogTitle>{selectedCase.title}</DialogTitle>
            <DialogContent>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                Industry: {selectedCase.industry}
              </Typography>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Challenge
              </Typography>
              <Typography paragraph>
                {selectedCase.challenge}
              </Typography>
              
              <Typography variant="h6" gutterBottom>
                Solution
              </Typography>
              <Typography paragraph>
                {selectedCase.solution}
              </Typography>
              
              <Typography variant="h6" gutterBottom>
                Outcome
              </Typography>
              <Typography paragraph>
                {selectedCase.outcome}
              </Typography>
              
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Tags:
                </Typography>
                {selectedCase.tags.map((tag) => (
                  <Chip key={tag} label={tag} sx={{ mr: 1, mb: 1 }} />
                ))}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCaseDialogOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      
      <Dialog open={simulationDialogOpen} onClose={() => setSimulationDialogOpen(false)} maxWidth="md" fullWidth>
        {selectedSimulation && (
          <>
            <DialogTitle>{selectedSimulation.title}</DialogTitle>
            <DialogContent>
              <Typography paragraph>
                {selectedSimulation.description}
              </Typography>
              
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Simulation Overview
                </Typography>
                <Typography paragraph>
                  This is an interactive simulation where you'll be presented with a scenario and asked to make decisions as a Scrum Master or team member.
                </Typography>
                <Typography paragraph>
                  Your choices will affect the outcome, and you'll receive feedback on your approach.
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  Difficulty: {selectedSimulation.difficulty} | Estimated Time: {selectedSimulation.estimatedTime}
                </Typography>
              </Box>
              
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Note: Full interactive simulations are under development. This is a preview of the feature.
                </Typography>
                <Button variant="contained" color="primary" disabled>
                  Start Simulation (Coming Soon)
                </Button>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSimulationDialogOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
    </>
  );
};

export default CaseStudies;
